/**
 * Whimsical waiting messages and tips shown while a submission is being judged.
 *
 * These live here rather than in the i18n locale files on purpose: they are a large
 * volume of flavour text that only the judging overlay needs, so keeping them in the
 * exercise bundle avoids bloating the shared translations.json that ships on every page.
 * They are also intentionally not kept count-matched across locales.
 */

type Locale = "en" | "nl";

const MESSAGES: Record<Locale, string[]> = {
    en: [
        "The judge is comparing notes...",
        "Warming up the test cases...",
        "Asking the compiler what it thinks...",
        "Sending your code into the arena...",
        "Checking if your solution survives first contact with reality...",
        "Rolling out the red carpet for your submission...",
        "Letting the judge put on its reading glasses...",
        "Feeding your code to the sandbox...",
        "Running the tests, one tiny drama at a time...",
        "Seeing what your code does when nobody is watching...",
        "Putting your code under a friendly microscope...",
        "The test cases are stretching their legs...",
        "Your code has entered the courtroom...",
        "The jury of edge cases is deliberating...",
        "Looking for sneaky off-by-one errors...",
        "Checking whether the examples were just the beginning...",
        "Giving your solution a polite but thorough interrogation...",
        "The compiler has entered the chat...",
        "Preparing the verdict...",
        "Your submission is having a moment with the judge...",
        "Checking whether 'works on my machine' also works here...",
        "Introducing your code to some very opinionated tests...",
        "Making sure your output behaves itself...",
        "Inspecting your solution from every angle...",
        "Letting the test cases ask the difficult questions...",
        "Taking your code for a quick obstacle course...",
        "Searching for hidden dragons in the input...",
        "Checking the corners of the problem statement...",
        "Giving your algorithm a little workout...",
        "Asking your program to show its work...",
        "Running your code through the Dodona obstacle course...",
        "Your submission is in the testing zone...",
        "Comparing your output with the secret scrolls...",
        "Looking very closely at the final newline...",
        "Trying the obvious cases, then the sneaky ones...",
        "The judge is checking the receipts...",
        "Putting on safety goggles before running your code...",
        "Preparing a small army of test cases...",
        "Letting the sandbox do sandbox things...",
        "Checking if your code can handle the plot twist...",
        "Your solution is meeting the edge cases...",
        "Watching your code make life choices...",
        "The tests are asking follow-up questions...",
        "Sending in the corner cases...",
        "Checking if your solution keeps its promises...",
        "Letting your algorithm explain itself...",
        "Running a tiny experiment with your code...",
        "Your code is being gently stress-tested...",
        "The judge is thinking very judge-like thoughts...",
        "Consulting the council of assertions...",
        "Running your code past the official answer key...",
        "Seeing if your program and the tests agree...",
        "The output comparator is warming up...",
        "Checking if every character is where it should be...",
        "Your code is on the witness stand...",
        "Cross-examining the output...",
        "The test cases are taking notes...",
        "Opening the envelope with the expected output...",
        "Checking if your solution sticks the landing...",
        "Letting the judge have a good look...",
        "Inviting the hidden tests to the party...",
        "Checking if the algorithm brought snacks for the edge cases...",
        "Running the boring cases first...",
        "Running the spicy cases next...",
        "Looking for tiny differences with big consequences...",
        "Comparing every character like it matters, because it does...",
        "The whitespace inspector is on duty...",
        "Checking whether the output has the right vibes and the right bytes...",
        "Your code is passing through the correctness scanner...",
        "Running the tests without mercy, but with fairness...",
        "The judge is being very calm and very precise...",
        "The verdict machine is humming...",
        "Loading the official expectations...",
        "Giving your solution a fair trial...",
        "Seeing how your code handles surprise guests...",
        "The test cases are lining up...",
        "Checking if your code can keep up...",
        "Letting your program do its thing...",
        "Your submission is being evaluated by highly trained electrons...",
        "The bits are marching into position...",
        "The judge is counting pixels. Just kidding. Probably.",
        "The test cases are whispering among themselves...",
        "Polishing the verdict...",
        "Waiting for the last test case to stop being dramatic...",
        "Your code is running laps around the sandbox...",
        "Checking if your solution remembered all the rules...",
        "The judge is looking for evidence of correctness...",
        "Testing the happy path and the grumpy path...",
        "Your program is taking the scenic route through the tests...",
        "Asking the edge cases how they feel about this...",
        "Checking if the solution still works when the input gets weird...",
        "Running tests in a very official-looking manner...",
        "The judge is sharpening its pencil...",
        "The compiler is clearing its throat...",
        "The test runner is tying its shoelaces...",
        "Your code is being measured twice and cut once...",
        "Comparing outputs with maximum seriousness...",
        "Checking if your answer matches the prophecy...",
        "The judge is decoding the mysteries of your submission...",
        "Running your code past the truth detector...",
        "Your solution is getting the full treatment...",
        "Inspecting the algorithmic machinery...",
        "Listening carefully to what your program prints...",
        "Waiting for your code to say the magic words...",
        "Your submission is taking the judge for a spin...",
        "The hidden tests have arrived fashionably late...",
        "Running your code through a maze of expectations...",
        "Checking if the solution generalizes beyond the sample input...",
        "Seeing whether your loop knows when to stop...",
        "Looking for semicolons in all the usual places...",
        "Checking if indentation is secretly the main character...",
        "The sandbox is doing science...",
        "The tests are conducting a very small investigation...",
        "Your code is getting a reality check...",
        "Comparing notes with the reference solution...",
        "Testing your code against the known universe...",
        "The judge is consulting the ancient documentation...",
        "Letting the test runner cook...",
        "Your program is currently negotiating with the input...",
        "Checking if your output tells the same story as ours...",
        "The judge is assembling the clues...",
        "Your code is being reviewed by a panel of very strict rubber ducks...",
        "Inviting the compiler, the runner, and the comparator to the same meeting...",
        "Waiting for the algorithm to reveal its true nature...",
        "The judge is checking if the math checks out...",
        "Preparing a verdict with just the right amount of suspense...",
        "The tests are politely disagreeing or enthusiastically nodding...",
        "Seeing if your code can handle the sequel...",
        "Giving the final test case a dramatic entrance...",
        "Your submission is in the hands of the test gremlins...",
        "The output checker is doing detective work...",
        "Running the code. Watching closely. Judging fairly.",
        "Compiling confidence...",
        "Running on pure logic and a little electricity...",
        "Your solution is being scanned for brilliance...",
        "The final newline is being consulted...",
        "The test cases demand answers...",
        "Trying to break your code gently...",
        "The judge is almost ready to make eye contact...",
        "Letting the machine do the marking...",
        "Checking if your assumptions match our assumptions...",
        "Bringing in the weird inputs...",
        "No bugs were harmed in the making of these tests. Probably.",
        "The judge is comparing your output character by character...",
        "The test runner is having a productive day...",
        "Your code is entering the correctness tunnel...",
        "The verdict is loading its dramatic pause...",
        "Giving your submission the official Dodona stare...",
        "The judge is checking the fine print...",
        "Your algorithm is currently under observation...",
        "Letting the tests poke your solution a bit...",
        "Checking if your code deserves confetti...",
        "The judge is preparing either confetti or feedback...",
        "Seeing whether your program speaks fluent output...",
        "The test cases are forming an orderly queue...",
        "Asking the difficult questions so your teacher doesn't have to...",
        "Your solution is doing a tiny exam of its own...",
        "The judge is checking if everything adds up...",
        "Your code is being politely challenged...",
        "Running tests at the speed of grading...",
        "The machine is judging. The humans are resting.",
        "The judge is putting your code through a plot twist...",
        "Your submission is currently unavailable for comment...",
        "Running the test suite with professional curiosity...",
        "The judge is reading the room, and the output...",
        "Checking if your solution can survive the hidden levels...",
        "The tests are unlocking the next boss fight...",
        "Your code is being compared to the answer key of destiny...",
        "Evaluating your solution with maximum fairness and minimal drama...",
        "The judge is almost done thinking...",
        "The last test case is crossing the finish line...",
        "Wrapping up the results...",
        "The verdict is approaching...",
        "Almost there. The judge is just checking one more thing...",
        "Final checks are happening behind the curtain...",
        "The result is getting dressed for its big reveal...",
    ],
    nl: [
        "De beoordelaar vergelijkt de notities...",
        "De testgevallen worden opgewarmd...",
        "We vragen de compiler wat die ervan vindt...",
        "Je code wordt de arena ingestuurd...",
        "We controleren of je oplossing het eerste contact met de realiteit overleeft...",
        "We rollen de rode loper uit voor je oplossing...",
        "De beoordelaar zet de leesbril op...",
        "Je code gaat de sandbox in...",
        "We draaien de tests, één klein drama tegelijk...",
        "We kijken wat je code doet als niemand kijkt...",
        "We leggen je code onder een vriendelijke microscoop...",
        "De testgevallen doen wat rekoefeningen...",
        "Je code heeft de rechtszaal betreden...",
        "De jury van randgevallen beraadslaagt...",
        "Op zoek naar geniepige off-by-onefouten...",
        "Je oplossing krijgt een beleefde maar grondige ondervraging...",
        "De compiler is de chat binnengekomen...",
        "Het verdict wordt voorbereid...",
        "Je oplossing heeft even een momentje met de beoordelaar...",
        'We controleren of "op mijn machine werkt het" ook hier werkt...',
        "We stellen je code voor aan een paar tests met een uitgesproken mening...",
        "We zorgen dat je uitvoer zich gedraagt...",
        "We bekijken je oplossing vanuit elke hoek...",
        "De testgevallen mogen de moeilijke vragen stellen...",
        "Je code loopt een korte hindernisbaan...",
        "We zoeken naar verborgen draken in de invoer...",
        "We controleren de hoekjes van de opgave...",
        "Je algoritme krijgt een kleine workout...",
        "We vragen je programma om te tonen wat het kan...",
        "Je code gaat door de Dodona-hindernisbaan...",
        "Je oplossing bevindt zich in de testzone...",
        "We vergelijken je uitvoer met de geheime boekrollen...",
        "We kijken héél nauwkeurig naar het laatste regeleinde...",
        "Eerst de voor de hand liggende gevallen, daarna de geniepige...",
        "We zetten een veiligheidsbril op voor we je code uitvoeren...",
        "Een klein leger testgevallen staat klaar...",
        "De sandbox doet sandboxdingen...",
        "We controleren of je code de plotwending aankan...",
        "Je oplossing maakt kennis met de randgevallen...",
        "We kijken hoe je code levenskeuzes maakt...",
        "De tests stellen bijvragen...",
        "De randgevallen worden ingezet...",
        "We controleren of je oplossing haar beloftes nakomt...",
        "Je algoritme mag zichzelf uitleggen...",
        "We voeren een klein experiment uit met je code...",
        "Je code wordt voorzichtig aan een stresstest onderworpen...",
        "De beoordelaar denkt heel beoordelaarachtige gedachten...",
        "We kijken of je programma en de tests het eens zijn...",
        "De uitvoervergelijker warmt op...",
        "We controleren of elk teken op de juiste plaats staat...",
        "Je code staat in de getuigenbank...",
        "De uitvoer wordt kruisverhoord...",
        "De testgevallen nemen notities...",
        "We openen de envelop met de verwachte uitvoer...",
        "We controleren of je oplossing netjes landt...",
        "De beoordelaar kijkt nog eens goed...",
        "We nodigen de verborgen tests uit op het feestje...",
        "We controleren of het algoritme snacks heeft meegebracht voor de randgevallen...",
        "Op zoek naar kleine verschillen met grote gevolgen...",
        "We vergelijken elk teken alsof het ertoe doet, want dat doet het...",
        "De witruimte-inspecteur heeft dienst...",
        "We controleren of de uitvoer de juiste vibe én de juiste bytes heeft...",
        "Je code gaat door de correctheidsscanner...",
        "We draaien de tests zonder genade, maar met rechtvaardigheid...",
        "De beoordelaar blijft heel kalm en heel precies...",
        "De verdictmachine zoemt...",
        "De officiële verwachtingen worden geladen...",
        "Je oplossing krijgt een eerlijk proces...",
        "We kijken hoe je code omgaat met onverwachte gasten...",
        "De testgevallen gaan in de rij staan...",
        "We controleren of je code kan volgen...",
        "Je programma mag zijn ding doen...",
        "Je oplossing wordt geëvalueerd door hoogopgeleide elektronen...",
        "De bits marcheren naar hun positie...",
        "De testgevallen fluisteren onder elkaar...",
        "Het verdict wordt opgepoetst...",
        "We wachten tot het laatste testgeval stopt met dramatisch doen...",
        "Je code rent rondjes door de sandbox...",
        "We controleren of je oplossing alle regels onthouden heeft...",
        "De beoordelaar zoekt bewijzen van correctheid...",
        "Je programma neemt de toeristische route door de tests...",
        "We vragen aan de randgevallen hoe ze zich hierbij voelen...",
        "We controleren of de oplossing nog werkt wanneer de invoer vreemd wordt...",
        "We voeren de tests uit op een zeer officieel ogende manier...",
        "De beoordelaar slijpt het potlood...",
        "De compiler schraapt de keel...",
        "De testrunner knoopt zijn veters...",
        "Je code wordt twee keer gemeten en één keer geknipt...",
        "We vergelijken uitvoer met maximale ernst...",
        "We controleren of je antwoord overeenkomt met de voorspelling...",
        "De beoordelaar ontcijfert de mysteries van je oplossing...",
        "Je code gaat langs de leugendetector...",
        "Je oplossing krijgt de volledige behandeling...",
        "We inspecteren de algoritmische machinerie...",
        "We luisteren aandachtig naar wat je programma afdrukt...",
        "We wachten tot je code de magische woorden zegt...",
        "Je oplossing neemt de beoordelaar mee voor een ritje...",
        "De verborgen tests komen modieus te laat aan...",
        "Je code loopt door een doolhof van verwachtingen...",
        "We controleren of de oplossing verder gaat dan de voorbeeldinvoer...",
        "We kijken of je lus weet wanneer ze moet stoppen...",
        "We controleren of inspringing stiekem de hoofdrol speelt...",
        "De sandbox doet aan wetenschap...",
        "De tests voeren een klein onderzoek uit...",
        "Je code krijgt een realitycheck...",
        "Notities vergelijken met de referentieoplossing...",
        "Je code testen tegen het bekende universum...",
        "De beoordelaar raadpleegt de oude documentatie...",
        "We laten de testrunner even koken...",
        "Je programma onderhandelt momenteel met de invoer...",
        "We controleren of je uitvoer hetzelfde verhaal vertelt als de onze...",
        "De beoordelaar verzamelt de aanwijzingen...",
        "Je code wordt nagekeken door een panel van zeer strenge badeendjes...",
        "We nodigen de compiler, de runner en de comparator uit voor dezelfde vergadering...",
        "Wachten tot het algoritme zijn ware aard onthult...",
        "De beoordelaar controleert of de wiskunde klopt...",
        "Een verdict voorbereiden met precies genoeg suspense...",
        "De tests zijn beleefd oneens of knikken enthousiast...",
        "We kijken of je code het vervolg aankan...",
        "Het laatste testgeval krijgt een dramatische entree...",
        "Je oplossing is in handen van de testgremlins...",
        "De outputchecker speelt detective...",
        "Code draaien. Aandachtig kijken. Eerlijk beoordelen.",
        "Zelfvertrouwen compileren...",
        "Draaiend op pure logica en een beetje elektriciteit...",
        "Je oplossing wordt gescand op genialiteit...",
        "Het laatste regeleinde wordt geraadpleegd...",
        "De testgevallen eisen antwoorden...",
        "We proberen je code zachtjes te breken...",
        "De beoordelaar is bijna klaar om oogcontact te maken...",
        "We laten de machine het nakijkwerk doen...",
        "We controleren of jouw aannames overeenkomen met de onze...",
        "De vreemde invoer wordt binnengebracht...",
        "Geen bugs raakten gewond bij het maken van deze tests. Waarschijnlijk.",
        "De beoordelaar vergelijkt je uitvoer teken voor teken...",
        "De testrunner heeft een productieve dag...",
        "Je code gaat de correctheidstunnel in...",
        "Het verdict laadt zijn dramatische pauze...",
        "Je oplossing krijgt de officiële Dodona-blik...",
        "De beoordelaar controleert de kleine lettertjes...",
        "Je algoritme staat momenteel onder observatie...",
        "De tests mogen je oplossing een beetje porren...",
        "We controleren of je code confetti verdient...",
        "De beoordelaar bereidt confetti of feedback voor...",
        "We kijken of je programma vloeiend uitvoer spreekt...",
        "De testgevallen vormen een ordelijke rij...",
        "De moeilijke vragen stellen, zodat je lesgever dat niet hoeft te doen...",
        "Je oplossing legt zelf een klein examen af...",
        "De beoordelaar controleert of alles klopt...",
        "Je code wordt beleefd uitgedaagd...",
        "Tests draaien op nakijksnelheid...",
        "De beoordelaar stuurt je code door een plotwending...",
        "Je oplossing is momenteel niet beschikbaar voor commentaar...",
        "De testsuite wordt met professionele nieuwsgierigheid uitgevoerd...",
        "De beoordelaar leest de sfeer, en de uitvoer...",
        "We controleren of je oplossing de verborgen levels overleeft...",
        "Je oplossing wordt geëvalueerd met maximale eerlijkheid en minimaal drama...",
        "De beoordelaar is bijna uitgedacht...",
        "Het laatste testgeval komt over de finish...",
        "De resultaten worden ingepakt...",
        "Het verdict komt dichterbij...",
        "Bijna klaar. De beoordelaar controleert nog één ding...",
        "Achter het gordijn gebeuren de laatste controles...",
        "Het resultaat kleedt zich aan voor de grote onthulling...",
    ],
};

const TIPS: Record<Locale, string[]> = {
    en: [
        "Tip: make sure your output format matches exactly.",
        "Tip: extra spaces or missing newlines can matter.",
        "Tip: print debugging is useful, but remember to remove debug output.",
        "Tip: test your code with values at the boundaries.",
        "Tip: if you are stuck, explain your code to a rubber duck or a classmate.",
        "Tip: split the problem into smaller parts before writing more code.",
        "Tip: when debugging, change one thing at a time.",
        "Tip: reread the examples and check how each output was produced.",
        "Tip: give variables names that explain what they contain.",
        "Tip: off-by-one errors love lists, ranges, and indexes.",
        "Tip: if your code crashes, the first error message is often the most useful clue.",
        "Tip: check whether you handle uppercase, lowercase, and punctuation correctly.",
        "Tip: avoid guessing. Use the feedback to form a hypothesis.",
        "Tip: use comments to explain why, not just what.",
        "Tip: if the result is almost correct, inspect formatting before changing the algorithm.",
        "Tip: compare expected and actual output carefully, even when they look the same.",
        "Tip: edge cases are not exceptions. They are part of the problem.",
        "Tip: make sure your solution does not depend only on the sample input.",
        "Tip: slow down for one minute before submitting again. It often saves five.",
        "Tip: code that is easy to read is usually easier to fix.",
        "Tip: if you get stuck, try solving the problem manually for one input.",
        "Tip: beware of changing a list while looping over it.",
        "Tip: initialize variables carefully. Bugs love forgotten starting values.",
        "Tip: do not ignore warnings. They are often future errors introducing themselves.",
        "Tip: you can hand in as many times as you like. Only your latest submission will be taken into account.",
    ],
    nl: [
        "Tip: zorg dat de vorm van je uitvoer exact overeenkomt.",
        "Tip: extra spaties of ontbrekende regeleindes kunnen een verschil maken.",
        "Tip: printdebugging is handig, maar vergeet je debuguitvoer niet te verwijderen.",
        "Tip: als je vastzit, leg je code dan uit aan een badeendje of een klasgenoot.",
        "Tip: verander tijdens het debuggen maar één ding tegelijk.",
        "Tip: geef variabelen namen die uitleggen wat ze bevatten.",
        "Tip: off-by-onefouten zijn dol op lijsten, bereiken en indexen.",
        "Tip: als je code crasht, is de eerste foutmelding vaak de nuttigste aanwijzing.",
        "Tip: controleer of je hoofdletters, kleine letters en leestekens correct verwerkt.",
        "Tip: gok niet zomaar. Gebruik de feedback om een hypothese te vormen.",
        "Tip: gebruik commentaar om uit te leggen waarom iets gebeurt, niet alleen wat er gebeurt.",
        "Tip: als het resultaat bijna klopt, controleer dan eerst de opmaak voordat je het algoritme aanpast.",
        "Tip: vergelijk de verwachte en de werkelijke uitvoer zorgvuldig, zelfs als ze hetzelfde lijken.",
        "Tip: randgevallen zijn geen uitzonderingen. Ze horen bij het probleem.",
        "Tip: zorg dat je oplossing niet alleen afhangt van de voorbeeldinvoer.",
        "Tip: neem één minuut de tijd voordat je opnieuw indient. Dat bespaart vaak vijf minuten.",
        "Tip: code die makkelijk leest, is meestal ook makkelijker te herstellen.",
        "Tip: als je vastzit, probeer het probleem dan handmatig op te lossen voor één invoer.",
        "Tip: pas op met het aanpassen van een lijst terwijl je erover itereert.",
        "Tip: initialiseer variabelen zorgvuldig. Bugs houden van vergeten beginwaarden.",
        "Tip: negeer waarschuwingen niet. Vaak zijn het toekomstige fouten die zich voorstellen.",
        "Tip: je mag zo vaak indienen als je wilt. Alleen je laatst ingediende oplossing telt mee.",
    ],
};

function pool(map: Record<Locale, string[]>, locale: string): string[] {
    return map[locale as Locale] ?? map.en;
}

export function judgingMessages(locale: string): string[] {
    return pool(MESSAGES, locale);
}

export function judgingTips(locale: string): string[] {
    return pool(TIPS, locale);
}
